/**
 * PATH: apps/ai/src/services/agents/agent-types.ts
 * PURPOSE: Shared types and auth helpers for the multi-agent AI orchestration layer
 */

import { getAuthCookie } from '@quotech/auth';

// ---------------------------------------------------------------------------
// Auth Helpers
// ---------------------------------------------------------------------------

export function getAccessToken(): string | null {
  const cookieToken = getAuthCookie();
  if (cookieToken) return cookieToken;
  const legacyKeys = ['IdToken', 'accessToken', 'accessTokennew', 'access_token', 'token'];
  for (const key of legacyKeys) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QueryIntent {
  type: 'data_lookup' | 'search' | 'analysis' | 'action' | 'general' | 'clarification';
  confidence: number;
  domain: 'jobs' | 'quotes' | 'invoices' | 'timesheets' | 'portfolio' | 'general';
  entities: ExtractedEntities;
  suggestedRefinement?: string;
}

export interface ExtractedEntities {
  jobIds?: number[];
  jobNames?: string[];
  clientNames?: string[];
  dateRange?: { start?: string; end?: string };
  metrics?: string[];
  searchTerms?: string[];
}

export interface ConversationContext {
  sessionId: string;
  turns: ConversationTurn[];
  activeJobIds: number[];
  activeQuoteId?: number;
  lastIntent?: QueryIntent;
  accumulatedEntities: ExtractedEntities;
}

export interface ConversationTurn {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  intent?: QueryIntent;
  citations?: Citation[];
  chainOfThought?: string;
}

export interface Citation {
  sourceType: string;
  sourceName: string;
  relevance: number;
  snippet?: string;
  sourceUrl?: string;
  sourceId?: string;
  jobId?: string | number;
  category?: string;
}

export interface AgentResponse {
  content: string;
  chainOfThought: string;
  citations: Citation[];
  intent: QueryIntent;
  needsRefinement: boolean;
  suggestedFollowUp?: string;
  model?: string;
  latencyMs?: number;
}

export interface StreamEvent {
  type: 'thinking' | 'cot' | 'content' | 'meta' | 'done' | 'error' | 'refinement';
  content?: string;
  intent?: QueryIntent;
  citations?: Citation[];
  model?: string;
  latencyMs?: number;
  error?: string;
  suggestedRefinement?: string;
}

export interface SkillMatch {
  skillId: string;
  confidence: number;
  params: Record<string, unknown>;
  canExecute: boolean;
  missingParams?: string[];
}
